import { getStringArrayValue } from '@intelligence/v3/filterControls/shared/valueUtils';
import { buildRobloxGenreFilterParam } from '@intelligence/v3/utils/buildRobloxGenreFilter';
import { buildRobloxReleaseDateFilterParam } from '@intelligence/v3/utils/robloxReleaseDateFilter';
import { getRangePickerValue } from '@intelligence/v3/filterControls/rangePicker/utils';
import { appendRobloxFetchDateParams } from '@intelligence/v3/utils/robloxFilterDate';

import {
  getDefaultFilterValues,
  ROBLOX_TOP_CHARTS_DEFAULT_PAGE_SIZE,
  ROBLOX_TOP_CHARTS_FIXED_FETCH_PARAMS,
} from '../constants';
import {
  buildRobloxTopChartsOrderBy,
  ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY,
} from '../config/robloxTopChartsSort';

import type { IMainGenre } from '@intelligence/types/common';
import type { IGenreSelectValue } from '@intelligence/components/GenreSelect/types';
import type { IRangePickerFilterValue } from '@intelligence/v3/filterControls/rangePicker/types';
import type { IRobloxTopChartsFilterValues } from '../types';
import type { IRobloxGenreFilterItem } from '@intelligence/v3/utils/buildRobloxGenreFilter';

export interface IRobloxTopChartsFetchParams {
  start_date?: string;
  end_date?: string;
  granularity?: IGranularity;
  metrics: string[];
  group_by: string[];
  use_entity_type: string;
  use_custom_fields: string[];
  markets: string[];
  page: number;
  page_size: number;
  order_by: string[];
  filter: {
    roblox_genre?: IRobloxGenreFilterItem[];
    start_release_date?: string;
    end_release_date?: string;
  };
  keyword?: string;
  game_ids?: string[];
  rank_game_ids?: string[];
}

interface IBuildRobloxTopChartsFetchParamsOptions {
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortOrder?: 'asc' | 'desc';
  keyword?: string;
  gameIds?: string[];
  /** 搜索后点击结果时使用，与 gameIds 互斥 */
  rankGameIds?: string[];
  robloxGenreOptions?: IMainGenre[];
  /** 指定查询指标；未传时使用表格默认全量指标 */
  metrics?: string[];
}

export const buildRobloxTopChartsFetchParams = (
  filterValues: IRobloxTopChartsFilterValues,
  options?: IBuildRobloxTopChartsFetchParamsOptions,
): IRobloxTopChartsFetchParams => {
  const defaults = getDefaultFilterValues();
  const sortKey = options?.sortKey || ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY;
  const sortOrder = options?.sortOrder || 'desc';
  const keyword = options?.keyword?.trim();
  const gameIds = options?.gameIds?.filter(Boolean);
  const rankGameIds = options?.rankGameIds?.filter(Boolean);
  const genreFilter = buildRobloxGenreFilterParam(
    (filterValues.genre ?? defaults.genre) as IGenreSelectValue | undefined,
    options?.robloxGenreOptions,
  );
  const releaseDateFilter = buildRobloxReleaseDateFilterParam(
    (filterValues.releaseTime ?? defaults.releaseTime) as IRangePickerFilterValue | undefined,
  );

  const params: IRobloxTopChartsFetchParams = appendRobloxFetchDateParams({
    metrics: options?.metrics?.length
      ? [...options.metrics]
      : [...ROBLOX_TOP_CHARTS_FIXED_FETCH_PARAMS.metrics],
    group_by: [...ROBLOX_TOP_CHARTS_FIXED_FETCH_PARAMS.group_by],
    use_entity_type: ROBLOX_TOP_CHARTS_FIXED_FETCH_PARAMS.use_entity_type,
    use_custom_fields: [...ROBLOX_TOP_CHARTS_FIXED_FETCH_PARAMS.use_custom_fields],
    markets: getStringArrayValue(filterValues.region ?? defaults.region),
    page: options?.page ?? 1,
    page_size: options?.pageSize ?? ROBLOX_TOP_CHARTS_DEFAULT_PAGE_SIZE,
    order_by: [buildRobloxTopChartsOrderBy(sortKey, sortOrder)],
    filter: {
      ...genreFilter,
      ...releaseDateFilter,
    },
  }, filterValues);

  if (keyword) {
    params.keyword = keyword;
  }

  if (rankGameIds && rankGameIds.length > 0) {
    params.rank_game_ids = rankGameIds;
  } else if (gameIds && gameIds.length > 0) {
    params.game_ids = gameIds;
  }

  const { granularity } = getRangePickerValue(filterValues.date ?? defaults.date);
  if (granularity) {
    params.granularity = granularity;
  }

  return params;
};
